/**
 * Notification type icons and color configuration.
 * Uses lucide-react icons for consistency with the app design.
 */

import type { NotificationType } from "../../services/notification.types";

interface NotificationStyle {
 bgColor: string;
 iconColor: string;
 borderColor: string;
 progressColor: string;
}

export const NOTIFICATION_STYLES: Record<NotificationType, NotificationStyle> = {
 success: {
 bgColor: "bg-green-50",
 iconColor: "text-green-600",
 borderColor: "border-green-300",
 progressColor: "bg-green-500",
 },
 info: {
 bgColor: "bg-blue-50",
 iconColor: "text-blue-500",
 borderColor: "border-blue-200",
 progressColor: "bg-blue-400",
 },
 warning: {
 bgColor: "bg-orange-50",
 iconColor: "text-orange-500",
 borderColor: "border-orange-300",
 progressColor: "bg-orange-500",
 },
 error: {
 bgColor: "bg-red-50",
 iconColor: "text-red-600",
 borderColor: "border-red-300",
 progressColor: "bg-red-500",
 },
 system: {
 bgColor: "bg-purple-50",
 iconColor: "text-purple-500",
 borderColor: "border-purple-200",
 progressColor: "bg-purple-400",
 },
 security: {
 bgColor: "bg-red-50",
 iconColor: "text-red-600",
 borderColor: "border-red-300",
 progressColor: "bg-red-500",
 },
 task: {
 bgColor: "bg-indigo-50",
 iconColor: "text-indigo-500",
 borderColor: "border-indigo-200",
 progressColor: "bg-indigo-400",
 },
 project: {
 bgColor: "bg-cyan-50",
 iconColor: "text-cyan-500",
 borderColor: "border-cyan-200",
 progressColor: "bg-cyan-400",
 },
 leave: {
 bgColor: "bg-orange-50",
 iconColor: "text-orange-500",
 borderColor: "border-orange-300",
 progressColor: "bg-orange-500",
 },
 time: {
 bgColor: "bg-teal-50",
 iconColor: "text-teal-500",
 borderColor: "border-teal-200",
 progressColor: "bg-teal-400",
 },
};

/** SVG icon component for each notification type */
export function NotificationIcon({ type, className ="h-5 w-5"}: { type: NotificationType; className?: string }) {
 const style = NOTIFICATION_STYLES[type];
 const iconClass =`${className} ${style.iconColor}`;

 switch (type) {
 case"success":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
 <polyline points="22 4 12 14.01 9 11.01"/>
 </svg>
 );
 case"info":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="10"/>
 <line x1="12"y1="16"x2="12"y2="12"/>
 <line x1="12"y1="8"x2="12.01"y2="8"/>
 </svg>
 );
 case"warning":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
 <line x1="12"y1="9"x2="12"y2="13"/>
 <line x1="12"y1="17"x2="12.01"y2="17"/>
 </svg>
 );
 case"error":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="10"/>
 <line x1="15"y1="9"x2="9"y2="15"/>
 <line x1="9"y1="9"x2="15"y2="15"/>
 </svg>
 );
 case"system":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <rect x="2"y="3"width="20"height="14"rx="2"ry="2"/>
 <line x1="8"y1="21"x2="16"y2="21"/>
 <line x1="12"y1="17"x2="12"y2="21"/>
 </svg>
 );
 case"security":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
 </svg>
 );
 case"task":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M9 11l3 3L22 4"/>
 <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
 </svg>
 );
 case"project":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
 </svg>
 );
 case"leave":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <rect x="3"y="4"width="18"height="18"rx="2"ry="2"/>
 <line x1="16"y1="2"x2="16"y2="6"/>
 <line x1="8"y1="2"x2="8"y2="6"/>
 <line x1="3"y1="10"x2="21"y2="10"/>
 </svg>
 );
 case"time":
 return (
 <svg className={iconClass} viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="10"/>
 <polyline points="12 6 12 12 16 14"/>
 </svg>
 );
 }
}
